import { FETCHING_STATUS } from '@/constants';
import { TRADE_TYPE } from '@/constants/trade';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedPair } from '@/entities/WrappedPair';
import { useTokenBalance } from '@/features/balance/hook';
import { useGlobalConfig } from '@/features/global/hooks';
import { batchPlaceScaledLimitOrder, placeCrossMarketOrder, placeLimitOrder, trade } from '@/features/trade/actions';
import {
  useCanPlaceCrossMarketOrder,
  useCrossMarketSimulation,
  useLimitFormState,
  useLimitPriceWarning,
  useLimitSimulation,
  useMarketSimulation,
  useScaleFormStateStatus,
  useTradeSide,
  useTradeStoreTransformed,
} from '@/features/trade/hooks';
import { useSimulateBatchPlaceScaledLimitOrder } from '@/features/trade/query';
import { useTradeStore } from '@/features/trade/store';
import { useAppProvider, useSDK } from '@/features/web3/hook';
import { useAppDispatch } from '@/hooks';
import { useTxNotification } from '@/hooks/useTxNotification';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import { useWalletSigner } from '@/hooks/web3/useWalletNetwork';
import { toWad } from '@/utils/numberUtil';
import { useCallback, useMemo } from 'react';

export function useTradeFooterFormState(tradeType: TRADE_TYPE, currentPair: WrappedPair | undefined) {
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const { data: scaleLimitSimulation } = useSimulateBatchPlaceScaledLimitOrder(chainId);
  const limitSimulation = useLimitSimulation(chainId);
  const tradeSimulation = useMarketSimulation(chainId);
  const crossMarginSimulation = useCrossMarketSimulation(chainId);
  const canPlaceCrossMarketOrder = useCanPlaceCrossMarketOrder(chainId);
  const limitPriceMsg = useLimitPriceWarning(chainId);
  const provider = useAppProvider();
  const sdk = useSDK(chainId);
  const dispatch = useAppDispatch();

  const { slippage, deadline } = useGlobalConfig(chainId);
  const signer = useWalletSigner();
  const marketFormState = useTradeStoreTransformed();
  const limitFormState = useLimitFormState(chainId);
  const tradeSide = useTradeSide(chainId);

  const notification = useTxNotification();
  const scaleLimitFormStateStatus = useScaleFormStateStatus(chainId);
  const { setTradeSize, setTradeQuoteSize, setTradePercent, setIsOpenMobileTradeDrawer } = useTradeStore();

  const tradePrice = useMemo(() => {
    if (tradeType === TRADE_TYPE.MARKET && tradeSimulation?.data?.tradePrice) {
      return tradeSimulation?.data?.tradePrice;
    } else if (tradeType === TRADE_TYPE.LIMIT) {
      return canPlaceCrossMarketOrder
        ? crossMarginSimulation?.data
          ? WrappedBigNumber.from(crossMarginSimulation?.data?.orderSimulation?.limitPrice || 0)
          : WrappedBigNumber.from(limitFormState.alignedPrice || 0)
        : limitSimulation?.data
        ? WrappedBigNumber.from(limitSimulation?.data?.limitPrice || limitFormState.alignedPrice || 0)
        : WrappedBigNumber.from(limitFormState.alignedPrice || 0);
    }
    return undefined;
  }, [
    canPlaceCrossMarketOrder,
    crossMarginSimulation?.data,
    limitFormState.alignedPrice,
    limitSimulation?.data,
    tradeSimulation?.data?.tradePrice,
    tradeType,
  ]);

  const marginToDeposit = useMemo(() => {
    if (tradeType === TRADE_TYPE.MARKET) {
      return tradeSimulation?.data?.marginToDeposit;
    } else if (tradeType === TRADE_TYPE.SCALE_LIMIT) {
      return scaleLimitSimulation?.data?.marginToDeposit;
    } else {
      return canPlaceCrossMarketOrder
        ? crossMarginSimulation?.data?.marginToDeposit
        : limitSimulation?.data?.marginToDeposit;
    }
  }, [
    tradeType,
    tradeSimulation?.data?.marginToDeposit,
    scaleLimitSimulation?.data?.marginToDeposit,
    canPlaceCrossMarketOrder,
    crossMarginSimulation?.data?.marginToDeposit,
    limitSimulation?.data?.marginToDeposit,
  ]);
  const walletBalance = useTokenBalance(currentPair?.rootInstrument?.quoteToken?.address, chainId);

  const walletNoBalance = useMemo(() => {
    return walletBalance.lt(marginToDeposit || 0);
  }, [marginToDeposit, walletBalance]);

  const isDisableBtn = useMemo(() => {
    if (walletNoBalance) return true;
    if (tradeType === TRADE_TYPE.LIMIT) {
      if (canPlaceCrossMarketOrder) return !!crossMarginSimulation?.message;
      return !!limitSimulation?.message || !!limitPriceMsg;
    }
    if (tradeType === TRADE_TYPE.SCALE_LIMIT) return !!scaleLimitSimulation?.message;

    return !!tradeSimulation?.message || tradeSimulation?.data?.additionalFee?.gt(0);
  }, [
    canPlaceCrossMarketOrder,
    crossMarginSimulation?.message,
    limitPriceMsg,
    limitSimulation?.message,
    scaleLimitSimulation?.message,
    tradeSimulation?.message,
    tradeType,
    walletNoBalance,
    tradeSimulation?.data?.additionalFee,
  ]);

  const onTradeMarket = useCallback(async () => {
    if (!chainId || !userAddr || !sdk || !currentPair || !signer) return;
    if (tradeType === TRADE_TYPE.MARKET && tradeSimulation?.data) {
      const baseAmount = toWad(marketFormState.tradeSize || '0');

      const result = await dispatch(
        trade({
          chainId,
          userAddr,
          signer,
          sdk: sdk,
          pair: currentPair,
          side: tradeSide,
          baseAmount,
          slippage: Number(slippage),
          deadline: Number(deadline),
          simulation: tradeSimulation,
          tradePrice: tradeSimulation?.data?.tradePrice,
          provider,
          sendTxNotification: notification.open,
        }),
      ).unwrap();
      if (result?.status === 1) {
        setIsOpenMobileTradeDrawer(false);
        setTradeSize('');
        setTradeQuoteSize('');
        setTradePercent(0);
      }
    }
  }, [
    chainId,
    userAddr,
    sdk,
    currentPair,
    signer,
    tradeType,
    tradeSimulation,
    dispatch,
    tradeSide,
    marketFormState.tradeSize,
    slippage,
    deadline,
    provider,
    notification.open,
    setIsOpenMobileTradeDrawer,
    setTradeSize,
    setTradeQuoteSize,
    setTradePercent,
  ]);

  const onTradeLimit = useCallback(async () => {
    if (!chainId || !userAddr || !sdk || !currentPair || !signer) return;
    if (tradeType === TRADE_TYPE.LIMIT && limitSimulation?.data) {
      try {
        const result = await dispatch(
          placeLimitOrder({
            chainId,
            signer,
            userAddr,
            sdkContext: sdk,
            pair: currentPair,
            simulation: limitSimulation?.data,
            deadline: Number(deadline),
            provider,
            side: tradeSide,
            sendTxNotification: notification?.open,
          }),
        ).unwrap();
        if (result?.status === 1) {
          setIsOpenMobileTradeDrawer(false);
          setTradeSize('');
          setTradeQuoteSize('');
          setTradePercent(0);
        }
      } catch (e) {
        console.log('🚀 ~ e:', e);
      }
    }
  }, [
    chainId,
    userAddr,
    sdk,
    currentPair,
    signer,
    tradeType,
    limitSimulation?.data,
    dispatch,
    deadline,
    provider,
    tradeSide,
    notification?.open,
    setTradeSize,
    setTradeQuoteSize,
    setTradePercent,
    setIsOpenMobileTradeDrawer,
  ]);

  const onTradeScaledLimit = useCallback(async () => {
    if (tradeType === TRADE_TYPE.SCALE_LIMIT && scaleLimitSimulation?.data) {
      if (scaleLimitFormStateStatus === FETCHING_STATUS.FETCHING) return;

      const baseAmountWad = toWad(marketFormState?.tradeSize || 0);

      const leverageWad = toWad(marketFormState.leverage);

      if (
        signer &&
        chainId &&
        userAddr &&
        sdk &&
        currentPair &&
        provider &&
        scaleLimitSimulation?.data &&
        !baseAmountWad.eq(0) &&
        deadline &&
        marketFormState.leverage
      ) {
        try {
          const result = await dispatch(
            batchPlaceScaledLimitOrder({
              signer,
              chainId,
              userAddr,
              sdkContext: sdk,
              pair: currentPair,
              provider,
              simulation: scaleLimitSimulation?.data,
              deadline: Number(deadline),
              side: tradeSide,
              leverageWad: leverageWad,
              baseSizeWad: baseAmountWad,
              sendTxNotification: notification?.open,
            }),
          ).unwrap();
          if (result?.status === 1) {
            setIsOpenMobileTradeDrawer(false);
            setTradeSize('');
            setTradeQuoteSize('');
            setTradePercent(0);
          }
        } catch (e) {
          console.log('🚀 ~ e:', e);
        }
      }
    }
  }, [
    scaleLimitSimulation?.data,
    tradeType,
    scaleLimitFormStateStatus,
    marketFormState?.tradeSize,
    marketFormState.leverage,
    signer,
    chainId,
    userAddr,
    sdk,
    currentPair,
    provider,
    deadline,
    tradeSide,
    notification?.open,
    setIsOpenMobileTradeDrawer,
    setTradeSize,
    setTradeQuoteSize,
    setTradePercent,
    dispatch,
  ]);

  const onTradeCrossMarket = useCallback(async () => {
    if (!chainId || !userAddr || !sdk || !currentPair || !signer) return;
    if (tradeType === TRADE_TYPE.LIMIT && crossMarginSimulation?.data) {
      const result = await dispatch(
        placeCrossMarketOrder({
          chainId,
          signer,
          userAddr,
          sdkContext: sdk,
          pair: currentPair,
          simulation: crossMarginSimulation?.data,
          provider,
          side: tradeSide,
          slippage: Number(slippage),
          deadline: Number(deadline),
          sendTxNotification: notification?.open,
        }),
      ).unwrap();
      if (result?.status === 1) {
        setIsOpenMobileTradeDrawer(false);
      }
    }
  }, [
    chainId,
    crossMarginSimulation?.data,
    currentPair,
    deadline,
    dispatch,
    notification?.open,
    provider,
    sdk,
    setIsOpenMobileTradeDrawer,
    signer,
    slippage,
    tradeSide,
    tradeType,
    userAddr,
  ]);

  return {
    marginToDeposit,
    isDisableBtn,
    tradePrice,
    onTradeMarket,
    onTradeLimit,
    onTradeScaledLimit,
    onTradeCrossMarket,
  };
}
